/**
 * The Mantine 9 theme object. Both SPAs load this one; neither defines a theme of its own
 * (docs/ui/00-README.md, docs/ui/02-theme.md).
 *
 * There is not a single raw colour in this file. Every colour comes from ./tokens, which is the
 * one file the no-raw-colour lint rule exempts.
 */
import {
  ActionIcon,
  Anchor,
  Button,
  Card,
  Menu,
  Modal,
  Paper,
  Select,
  Textarea,
  TextInput,
  Tooltip,
  createTheme,
  defaultVariantColorsResolver,
  mergeThemeOverrides,
} from '@mantine/core';
import type {
  AlertProps,
  MantineColorsTuple,
  MantineThemeOverride,
  VariantColorsResolver,
} from '@mantine/core';
import { englishUiText } from '../texts/ui-texts';
import type { UiTranslate } from '../texts/ui-texts';
import { absolute, gold, grey } from './tokens';

/**
 * Mantine requires at least ten shades per colour — fewer is a TypeScript error
 * (`MantineColorsTuple` is `readonly [string x10, ...string[]]`). The brand defines exactly two
 * golds, so ./tokens builds the ramp and anchors it on them. Both arrays are `as const`, which is
 * what makes them assignable here without a cast.
 */
const moon: MantineColorsTuple = gold;
const gray: MantineColorsTuple = grey;

/**
 * THIS RESOLVER IS LOAD-BEARING, NOT A NICETY.
 *
 * Mantine's default resolver builds every hover state with `darken()` and `lighten()`. Both call
 * `toRgba()`, and `toRgba()` returns BLACK for anything that is not hex, rgb or hsl — with no
 * throw and no warning. Verified first-hand in the shipped source of @mantine/core 9.5.2:
 *
 *   esm/core/MantineProvider/color-functions/to-rgba/to-rgba.mjs
 *     function toRgba(color) {
 *       if (isHexColor(color)) return hexToRgba(color);
 *       if (color.startsWith("rgb")) return rgbStringToRgba(color);
 *       if (color.startsWith("hsl")) return hslStringToRgba(color);
 *       return { r: 0, g: 0, b: 0, a: 1 };      <-- every oklch() lands here
 *     }
 *   esm/core/MantineProvider/color-functions/darken/darken.mjs
 *     const { r, g, b, a } = toRgba(color); ... return `rgba(${dark(r)}, …)`
 *
 * So with an OKLCH palette and the default resolver, every hover colour in both apps quietly
 * becomes black-derived and nothing reports it. We resolve every state from brand tokens instead
 * and never call either function. `rgba`/`alpha` ARE OKLCH-aware and would be safe, but we do not
 * need them: the `/ 0.14` slot in an `oklch()` string does the same job with no function call.
 *
 * web/ui/test/theme/variant-color-resolver.spec.tsx guards this. If you delete the resolver from
 * the theme below, that test goes red — which is the only reason the failure is not silent.
 */
export const variantColorResolver: VariantColorsResolver = (input) => {
  const base = defaultVariantColorsResolver(input);

  if (input.color === 'moon' || input.color === undefined) {
    if (input.variant === 'filled') {
      return {
        background: 'oklch(var(--mx-accent))',
        hover: 'oklch(var(--mx-accent-hover))',
        border: 'none',
        color: 'oklch(var(--mx-on-accent))',
      };
    }
    if (input.variant === 'light') {
      return {
        background: 'oklch(var(--mx-accent) / 0.14)',
        hover: 'oklch(var(--mx-accent) / 0.22)',
        border: 'none',
        color: 'oklch(var(--mx-accent-hover))', // not `accent`: see the note after the theme
      };
    }
    if (input.variant === 'outline' || input.variant === 'subtle') {
      return {
        background: 'transparent',
        hover: 'oklch(var(--mx-accent) / 0.10)',
        border: input.variant === 'outline' ? '1px solid oklch(var(--mx-accent) / 0.45)' : 'none',
        color: 'oklch(var(--mx-accent-hover))', // as `light`: see the note after the theme
      };
    }
  }
  const tone = input.color === undefined ? undefined : STOCK_TONES[input.color];
  return (tone === undefined ? undefined : toneVariant(tone, input.variant)) ?? base;
};

export const theme = createTheme({
  /* ---- colour ---------------------------------------------------------------------------- */
  colors: { moon, gray },
  primaryColor: 'moon',
  /**
   * NOT Mantine's default { light: 6, dark: 8 }. MoonX's dark-theme gold is BRIGHT (80%
   * lightness) and its light-theme gold is DEEP (52%), so the dark scheme takes the LIGHTER
   * index. This inversion looks wrong and it is right — do not "fix" it.
   */
  primaryShade: { light: 7, dark: 3 },
  variantColorResolver,
  /**
   * Off. The ink on gold is set explicitly through `--mx-on-accent`; letting Mantine choose
   * between theme.white and theme.black would reintroduce the exact 3.72:1 failure recorded in
   * the KNOWN CONTRAST DEBT note in ./tokens.
   */
  autoContrast: false,
  white: absolute.white,
  black: absolute.black,

  /* ---- type ------------------------------------------------------------------------------ */
  fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", sans-serif',
  fontFamilyMonospace: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  /**
   * `fontWeights` is new in v9, and v9 moved `medium` from 500 to 600. The cabinet is effectively
   * a two-weight system: 800 headings (63.8% of weight declarations) and 700 labels (29.6%).
   */
  fontWeights: { regular: '400', medium: '600', bold: '700' },
  headings: {
    fontFamily: 'Inter, system-ui, sans-serif',
    fontWeight: '800',
    textWrap: 'pretty',
    sizes: {
      h1: { fontSize: 'clamp(1.625rem, 4vw, 2.5rem)', lineHeight: '1.1', fontWeight: '800' },
      h2: { fontSize: '1.5rem', lineHeight: '1.2', fontWeight: '800' },
      h3: { fontSize: '1.25rem', lineHeight: '1.3', fontWeight: '800' },
      h4: { fontSize: '1.0625rem', lineHeight: '1.3', fontWeight: '800' },
      h5: { fontSize: '0.9375rem', lineHeight: '1.4', fontWeight: '700' },
      h6: { fontSize: '0.8125rem', lineHeight: '1.4', fontWeight: '700' },
    },
  },
  /**
   * Mantine's five slots mapped onto the brand roles. `sm` is 13px — the cabinet's real body
   * size, 21.3% of all its text. The named roles with no Mantine slot (`display`, `prose`,
   * `lead`, `nano`) are emitted as plain CSS variables by ./css-variables.
   */
  fontSizes: {
    xs: '0.6875rem', // 11px - micro / eyebrow
    sm: '0.8125rem', // 13px - ui, the cabinet default
    md: '0.875rem', // 14px - body
    lg: '0.9375rem', // 15px - lead
    xl: '1.0625rem', // 17px - title
  },
  lineHeights: { xs: '1.3', sm: '1.5', md: '1.65', lg: '1.6', xl: '1.3' },

  /* ---- metrics --------------------------------------------------------------------------- */
  /** Five of the twelve steps in ./tokens — the ones Mantine components ask for by name. */
  spacing: {
    xs: '0.375rem', // 6
    sm: '0.5rem', // 8
    md: '0.75rem', // 12  <- the commonest gap in the cabinet, 10.7% of all spacing
    lg: '1rem', // 16
    xl: '1.5rem', // 24
  },
  radius: {
    xs: '0.3125rem', // 5
    sm: '0.5rem', // 8
    md: '0.625rem', // 10
    lg: '0.75rem', // 12
    xl: '1rem', // 16
  },
  /**
   * v9 changed the default from `sm` to `md`. Stated explicitly so the value is a decision rather
   * than a version artefact. 16px is the cabinet's card radius (26 of 139 declarations).
   */
  defaultRadius: 'xl',
  shadows: {
    xs: 'var(--mx-elev-1)',
    sm: 'var(--mx-elev-1)',
    md: 'var(--mx-elev-2)',
    lg: 'var(--mx-elev-3)',
    xl: 'var(--mx-elev-3)',
  },
  /**
   * Mantine breakpoints are in `em`, not px. 820px is the prototype's own desktop/mobile split
   * (`window.innerWidth < 820`); 51.25em is 820px at a 16px root.
   */
  breakpoints: { xs: '36em', sm: '48em', md: '51.25em', lg: '64em', xl: '75em' },

  /* ---- behaviour ------------------------------------------------------------------------- */
  /**
   * 'auto' shows the ring for keyboard users only — the native behaviour, and what the brand's
   * own :focus-visible rule already does. Do NOT also set `focusClassName`: it overrides
   * focusRing entirely.
   */
  focusRing: 'auto',
  /**
   * Honours the reader's "reduce motion" system setting inside Mantine's own transitions. The
   * global CSS rule in ./global.css covers everything else.
   */
  respectReducedMotion: true,
  cursorType: 'pointer',

  /* ---- per-component defaults ------------------------------------------------------------ */
  components: {
    Button: Button.extend({ defaultProps: { radius: 'md', fw: 700 } }),
    ActionIcon: ActionIcon.extend({ defaultProps: { variant: 'subtle', radius: 'md' } }),

    /**
     * ★ WCAG 2.2 AA 1.4.1, AND IT IS MEASURED RATHER THAN ASSUMED. Mantine's `Anchor` defaults to
     * `underline="hover"`, which distinguishes a link from its surrounding text BY COLOUR ALONE
     * until you point at it. axe reports that as `link-in-text-block`, impact `serious`, and it did
     * on the cabinet shell's own `/enter` link the first time seam 11's accessibility leg was ever
     * executed - in BOTH colour schemes, so it is not the light theme's recorded contrast debt.
     *
     * It is fixed here and not at the fifty-five call sites, because a per-site fix is one a new
     * screen silently opts out of. `size` and `c` are left to the caller; only the thing the
     * standard requires is pinned.
     */
    Anchor: Anchor.extend({ defaultProps: { underline: 'always' } }),

    /**
     * `withAsterisk` marks a field required VISUALLY without adding the native `required`
     * attribute. With native `required` the browser blocks submit before @mantine/form's
     * resolver runs, producing a second, unstyled error channel. Always this, never `required`,
     * on any form we validate ourselves.
     */
    TextInput: TextInput.extend({ defaultProps: { withAsterisk: false, size: 'sm' } }),
    Textarea: Textarea.extend({ defaultProps: { size: 'sm', autosize: true, minRows: 3 } }),
    Select: Select.extend({ defaultProps: { size: 'sm', checkIconPosition: 'right' } }),

    /**
     * Mantine tooltips do NOT open on focus by default, so a keyboard user never sees the content
     * of an icon-only control — a WCAG 1.4.13 failure on every one of them. Turn focus events on
     * here, once, rather than per usage.
     */
    Tooltip: Tooltip.extend({
      defaultProps: { events: { hover: true, focus: true, touch: true }, withArrow: true },
    }),

    /** `trigger="hover"` is documented as NOT keyboard-accessible. Pin the safe default. */
    Menu: Menu.extend({ defaultProps: { trigger: 'click', shadow: 'md', radius: 'md' } }),

    Modal: Modal.extend({ defaultProps: { centered: true, radius: 'xl', shadow: 'lg' } }),

    /**
     * ★ EVERY DIALOG'S × HAS A NAME, AND SO DOES EVERY DISMISSIBLE ALERT'S. Mantine draws both as a
     * `CloseButton` with no text and no `aria-label` unless the caller passes one - axe's
     * `button-name` the moment either appears. Found live on 2026-09-23: none of the fourteen
     * dialogs in the two apps passed a label, and two alerts did not either. Named here, once, as
     * `Anchor` is above: a per-site label is one a new screen silently leaves out. On
     * `ModalCloseButton` rather than on `Modal`'s `closeButtonProps`, so a caller that passes its own
     * `closeButtonProps` keeps the name; a caller's own label still wins over both defaults.
     *
     * ★ THE FIRST ATTEMPT, `8d5d653`, WAS REVERTED FOR A CAUSE THAT WAS NOT ITS OWN. It looked as if
     * the label stopped dialogs opening. It did not: a HIDDEN browser pane pauses
     * `requestAnimationFrame`, and a Mantine 9 dialog leaves its `exited` transition only inside a
     * frame, label or no label - measured 2026-09-23 with no label anywhere in the code.
     */
    ...closeButtonNames(englishUiText),

    /**
     * ★ A DIALOG'S HEADER IS NOT A SECOND PAGE BANNER. Mantine draws the title row as `<header>`
     * inside `<section role="dialog">`, and the explicit `dialog` role takes the section out of the
     * sectioning elements that would scope it - so the row is exposed as a `banner` landmark beside
     * each app's own top bar, which axe reports as `landmark-no-duplicate-banner` on every open
     * dialog (measured 2026-09-24 in headless Chromium). The row is layout for a title and a ×, and
     * `none` is one of the roles `<header>` permits; the dialog keeps its name from the title.
     */
    ModalHeader: Modal.Header.extend({ defaultProps: { role: 'none' } }),

    /**
     * NO SHADOW ON A PAPER OR A CARD, and that is the brand's look rather than an omission: the
     * cabinet separates surfaces with a hairline and a lightness step, never a drop shadow —
     * measured at a 16:1 ratio of pixel-valued borders to shadows (docs/ui/01-tokens.md §6).
     *
     * The track drew this as `shadow: undefined` (docs/ui/02-theme.md). Written out it is
     * a type error here, because 02-nfr.md rules `exactOptionalPropertyTypes` for the repo's
     * one strict tsconfig and that flag makes an explicit `undefined` illegal for an optional
     * prop. Omitting the key is exactly the same instruction to Mantine — an absent `shadow`
     * yields no `box-shadow` — so the decision is carried here in words instead of in a value.
     */
    Paper: Paper.extend({ defaultProps: { radius: 'xl', withBorder: true } }),
    Card: Card.extend({ defaultProps: { radius: 'xl', withBorder: true } }),
  },
});

/**
 * THE INKS THE RESOLVER ABOVE DOES NOT TAKE FROM MANTINE, AND THE NUMBERS THAT CHOSE THEM - derived
 * by the method of web/ui/test/theme/derive.ts, asserted in its variant-color-resolver.spec.tsx.
 *
 * `moon` INKS IN `accent-hover`, NOT `accent`, IN `light`, `outline` AND `subtle`. Gold text on
 * its own gold tint read 4.21 on the light page and 3.46 on surface2 - the terms panel's moon
 * Badge sits on exactly that - because `accent` is the gold nearest each scheme's ground.
 * `accent-hover` is the brand's darker gold in light and its lighter one in dark, the step AWAY
 * from the ground in both, so it is the gold that reads there: light 5.68 on the page, 6.14 on
 * white, 4.67 on surface2; dark 8.53 (a hovered row) to 11.87 (the page), from 6.53-9.08. The same
 * held `outline` and `subtle` back: hovered on the light page - the DataTable pager - 4.44, on a
 * hovered row 4.08, and 4.10 at rest on surface2; in `accent-hover` 5.99, 5.51 and 5.53 (4.90
 * hovered), and 9.34 or more in dark. ⚠ A BOUND, NOT A REPAIR: the light-variant HOVER tint on
 * surface2 is 4.22, and gold on a gold wash over surface4 4.04-4.25 - the palette has no darker
 * gold. Nothing sits on either in the two apps.
 *
 * FIVE STOCK COLOURS TAKE A BRAND TONE, in the table below, for the variants the apps put under
 * text; every other stock variant stays Mantine's (and ./global.css's). `outline` and `subtle` ink
 * in the tone and draw the moon branch's 0.45 border: Mantine's light scheme inked `outline` at the
 * theme's shade 7 - blue 4.20, orange 3.04, green 2.75 on white - and now info 7.12, the warning
 * ink 7.48, success 6.54; dark 6.69 or more on every fill, a hovered row included.
 *
 * FILLED RED IS THE BRAND'S DANGER PAIR. Mantine inks every filled colour white, and the inverted
 * `primaryShade` above hands each STOCK colour its shade 3 in dark and 7 in light: white on red.3
 * is 1.84 (2.32 hovered, on red.4) and on red.7 3.84. Red is the only colour besides `moon` either
 * app fills - eight back-office buttons: "Close item", "Retire it", the content board's destructive
 * confirm and five `ConfirmDialog confirmColor="red"` callers. `on-danger` on `danger` is 5.79 in
 * dark and 5.88 in light. The hover mixes 12% of the scheme's own text into the fill, which moves
 * it AWAY from the ink in both schemes - lighter in dark, darker in light - to 6.78 and 7.01.
 *
 * ★ A STOCK TONE'S TINT IS MIXED INTO THE CARD COLOUR, NOT LAID OVER WHAT IS BENEATH. `danger` on a
 * translucent wash of itself stays above 4.5 only to 0.06 over a hovered row in dark and 0.07 in
 * light, and the money pills ARE red light Badges in DataTable rows, which highlight on hover -
 * Mantine's red fill there was opaque, so a wash would have regressed them. `color-mix(in srgb,
 * tone P%, var(--mantine-color-body))` is exactly that wash over a Paper, but opaque, so the ground
 * never enters the sum. Red `light` is 10% at rest and 16% hovered (light 5.17 and 4.68, dark 5.50
 * and 5.09; its old hover, red.2, was 3.76), and every `outline` and `subtle` hover 10% (danger,
 * the weakest, 5.17 and 5.50).
 */
interface StockTone {
  /** The brand triplet its tints and its border are drawn in. */
  readonly tone: string;
  /** The brand triplet its text is drawn in: the tone, or the tone's per-scheme ink alias. */
  readonly ink: string;
  /** The variants the resolver draws for it; any other variant is Mantine's own. */
  readonly variants: readonly string[];
}

const STOCK_TONES: Readonly<Record<string, StockTone>> = {
  red: { tone: 'danger', ink: 'danger', variants: ['filled', 'light', 'outline', 'subtle'] },
  green: { tone: 'success', ink: 'success', variants: ['outline', 'subtle'] },
  blue: { tone: 'info', ink: 'info', variants: ['outline', 'subtle'] },
  orange: { tone: 'warning', ink: 'warning-ink', variants: ['outline', 'subtle'] },
  yellow: { tone: 'warning', ink: 'warning-ink', variants: ['outline', 'subtle'] },
};

/** The tone mixed into the card colour: an opaque tint, whatever the ground beneath it. */
function mixed(tone: string, percent: number): string {
  const colour = `oklch(var(--mx-${tone}))`;
  return `color-mix(in srgb, ${colour} ${String(percent)}%, var(--mantine-color-body))`;
}

function toneVariant(
  { tone, ink, variants }: StockTone,
  variant: string,
): ReturnType<VariantColorsResolver> | undefined {
  if (!variants.includes(variant)) {
    return undefined;
  }
  if (variant === 'filled') {
    // Only `danger` is filled, and only it has an ink of its own for its fill, `on-danger`.
    return {
      background: `oklch(var(--mx-${tone}))`,
      hover: `color-mix(in oklch, oklch(var(--mx-${tone})) 88%, oklch(var(--mx-text)))`,
      border: 'none',
      color: `oklch(var(--mx-on-${tone}))`,
    };
  }
  return {
    background: variant === 'light' ? mixed(tone, 10) : 'transparent',
    hover: mixed(tone, variant === 'light' ? 16 : 10),
    border: variant === 'outline' ? `1px solid oklch(var(--mx-${tone}) / 0.45)` : 'none',
    color: `oklch(var(--mx-${ink}))`,
  };
}

/**
 * The names of a dialog's × and a dismissible alert's (the `ModalCloseButton` entry above says why
 * the theme names them), in the words `translate` gives. A function declaration, so the theme above
 * can call it before this line.
 */
function closeButtonNames(translate: UiTranslate) {
  return {
    ModalCloseButton: Modal.CloseButton.extend({
      defaultProps: { 'aria-label': translate('dialog.close') },
    }),
    // A plain entry, as `.extend` is only a typing identity: `AlertProps` checks the one prop.
    Alert: {
      defaultProps: { closeButtonLabel: translate('dialog.dismiss') } satisfies Partial<AlertProps>,
    },
  };
}

/** The theme with those two names in an app's own language - what `AppProviders` mounts when given one. */
export function themeSpeaking(translate: UiTranslate): MantineThemeOverride {
  return mergeThemeOverrides(theme, { components: closeButtonNames(translate) });
}
